import { FiPlus, FiTrash2, FiClipboard } from "react-icons/fi";
import { Prescription, ExerciseInPlan } from "../../../../types";
import { ExerciseRow } from "./ExerciseRow";

interface PrescriptionSectionProps {
  prescriptions: Prescription[];
  professionalId: string;
  onAddPrescription: () => void;
  onRemovePrescription: (index: number) => void;
  onUpdatePrescriptionName: (index: number, newName: string) => void;
  onUpdatePrescriptionObservation: (index: number, observation: string) => void;
  onAddExercise: (prescIndex: number) => void;
  onUpdateExercise: (
    prescIndex: number,
    exerIndex: number,
    field: keyof ExerciseInPlan,
    value: string | number,
  ) => void;
  onRemoveExercise: (prescIndex: number, exerIndex: number) => void;
  prescriptionsEndRef: React.RefObject<HTMLDivElement>;
}

export const PrescriptionSection = ({
  prescriptions,
  professionalId,
  onAddPrescription,
  onRemovePrescription,
  onUpdatePrescriptionName,
  onUpdatePrescriptionObservation,
  onAddExercise,
  onUpdateExercise,
  onRemoveExercise,
  prescriptionsEndRef,
}: PrescriptionSectionProps) => {
  const handleRemovePrescription = (index: number) => {
    if (globalThis.confirm("Tem certeza que deseja remover este treino?")) {
      onRemovePrescription(index);
    }
  };

  return (
    <section
      aria-label="Seção de treinos do plano"
      className="space-y-3 min-w-0 overflow-x-hidden sm:space-y-4 md:space-y-5"
    >
      {/* Header - Mobile First */}
      <div className="space-y-2 min-w-0 rounded-xl border border-stroke bg-white p-3 dark:border-strokedark dark:bg-boxdark sm:space-y-3 sm:p-4 md:flex md:items-center md:justify-between md:space-y-0 md:rounded-2xl md:p-5">
        <div className="space-y-1">
          <h4 className="text-base font-bold text-gray-900 dark:text-white sm:text-lg md:text-xl">
            Treinos do Plano
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
            Crie e organize os treinos por letras (A, B, C...) ou dias da semana.
          </p>
          <span className="inline-flex w-fit items-center rounded-full bg-purple-100 px-2.5 py-1 text-[11px] font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            {prescriptions.length} {prescriptions.length === 1 ? "treino" : "treinos"}
          </span>
        </div>
        <button
          type="button"
          onClick={onAddPrescription}
          aria-label="Adicionar novo treino ao plano"
          className="inline-flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl bg-purple-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition-all touch-manipulation active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/50 sm:min-h-0 sm:px-4 sm:py-3 md:w-auto md:px-5 md:hover:bg-purple-700 md:hover:shadow-md"
        >
          <FiPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
          Adicionar Treino
        </button>
      </div>

      {/* Prescription Cards - Mobile First */}
      {prescriptions.map((presc, pIndex) => (
        <div
          key={presc.id || `prescription-${pIndex}`}
          className="group relative min-w-0 overflow-x-hidden overflow-y-visible rounded-xl border border-stroke bg-white p-3.5 shadow-sm transition-all dark:border-strokedark dark:bg-boxdark sm:rounded-xl sm:p-4 md:rounded-2xl md:p-6"
        >
          {/* Header do Treino - Mobile First */}
          <div className="relative mb-3 space-y-2.5 border-b border-gray-200 pb-3 dark:border-gray-700 sm:mb-4 sm:space-y-3 sm:pb-4 md:mb-6 md:flex md:items-center md:gap-3 md:space-y-0">
            {/* Badge e Delete Button Row - Mobile First */}
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-600 text-base font-bold text-white shadow-sm sm:h-10 sm:w-10 sm:rounded-lg md:h-12 md:w-12 md:rounded-xl md:text-xl">
                {String.fromCodePoint(65 + pIndex)}
              </div>
              <button
                type="button"
                onClick={() => handleRemovePrescription(pIndex)}
                aria-label={`Remover treino ${presc.name || String.fromCodePoint(65 + pIndex)}`}
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-2 text-red-600 transition-all touch-manipulation active:scale-95 active:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/40 dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 md:hidden sm:min-h-0 sm:rounded-lg sm:px-3 sm:py-2.5"
                title="Remover treino"
              >
                <FiTrash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="text-xs font-semibold">Remover</span>
              </button>
            </div>

            {/* Input Field - Mobile First */}
            <input
              type="text"
              value={presc.name}
              onChange={(e) => onUpdatePrescriptionName(pIndex, e.target.value)}
              className="w-full min-h-[48px] box-border max-w-full rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 text-sm font-bold outline-none transition focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary touch-manipulation min-w-0 sm:rounded-lg sm:px-3 sm:py-2.5 md:flex-1 md:rounded-xl md:px-4 md:py-3 md:text-base"
              placeholder="Nome do Treino (ex: Treino A)"
            />

            {/* Delete Button - Desktop Only */}
            <button
              type="button"
              onClick={() => handleRemovePrescription(pIndex)}
              aria-label={`Remover treino ${presc.name || String.fromCodePoint(65 + pIndex)}`}
              className="hidden md:flex md:items-center md:justify-center md:rounded-xl md:border md:border-red-200 md:p-3 md:text-red-600 md:transition-all md:hover:border-red-300 md:hover:bg-red-50 md:focus-visible:outline-none md:focus-visible:ring-2 md:focus-visible:ring-red-400/40 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Remover treino"
            >
              <FiTrash2 size={20} />
            </button>
          </div>

          <div className="mb-3 sm:mb-4 md:mb-5">
            <label
              htmlFor={`prescription-observation-${pIndex}`}
              className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Observações do treino
            </label>
            <textarea
              id={`prescription-observation-${pIndex}`}
              value={presc.observation || ""}
              onChange={(e) =>
                onUpdatePrescriptionObservation(pIndex, e.target.value)
              }
              rows={3}
              placeholder="Ex: foco em execução controlada e descanso ativo."
              className="w-full min-h-[96px] rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 text-sm outline-none transition focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary sm:rounded-lg md:rounded-xl"
            />
          </div>

          {/* Exercícios - Mobile First */}
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            {/* Header Labels - Hidden on Mobile, Show on Desktop */}
            {presc.exercises.length > 0 && (
              <div className="hidden md:grid mb-3 grid-cols-12 gap-3 px-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 min-w-0 overflow-x-hidden">
                <div className="col-span-3">Exercício</div>
                <div className="col-span-2 text-center">Séries</div>
                <div className="col-span-2 text-center">Reps</div>
                <div className="col-span-2 text-center">Carga</div>
                <div className="col-span-1 text-center">Int.</div>
                <div className="col-span-1 text-center">Imagem</div>
                <div className="col-span-1"></div>
              </div>
            )}

            {presc.exercises.map((ex, eIndex) => (
              <ExerciseRow
                key={`exercise-${ex.id || ex.name}-${eIndex}`}
                exercise={ex}
                prescIndex={pIndex}
                exerIndex={eIndex}
                professionalId={professionalId}
                onUpdate={(field, value) =>
                  onUpdateExercise(pIndex, eIndex, field, value)
                }
                onRemove={() => onRemoveExercise(pIndex, eIndex)}
              />
            ))}

            {/* Botão Adicionar Exercício - Mobile First */}
            <button
              type="button"
              onClick={() => onAddExercise(pIndex)}
              aria-label={`Adicionar exercício ao treino ${presc.name || String.fromCodePoint(65 + pIndex)}`}
              className="sticky bottom-2 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 py-2.5 text-xs font-semibold text-purple-700 transition-all touch-manipulation active:scale-[0.98] active:border-purple-400 active:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/40 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-400 dark:active:bg-purple-900/30 sm:static sm:min-h-0 sm:rounded-lg sm:py-3 sm:text-sm md:rounded-xl md:py-4 md:hover:border-purple-400 md:hover:bg-purple-50 md:dark:hover:bg-purple-900/20"
            >
              <FiPlus size={14} className="sm:w-4 sm:h-4" /> Adicionar Exercício
            </button>
          </div>
        </div>
      ))}

      {/* Ref para scroll automático */}
      <div ref={prescriptionsEndRef} />

      {/* Estado Vazio - Mobile First */}
      {prescriptions.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white px-3 py-8 text-center dark:border-gray-700 dark:bg-boxdark sm:rounded-xl sm:px-4 sm:py-12 md:rounded-2xl md:py-16">
          <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-4 dark:from-purple-900/30 dark:to-purple-900/30 sm:p-5 md:p-6">
            <FiClipboard
              size={32}
              className="w-8 h-8 text-gray-400 dark:text-gray-600 sm:w-10 sm:h-10 md:w-12 md:h-12"
            />
          </div>
          <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-gray-300 sm:mt-4 sm:text-base md:mt-5 md:text-lg">
            Nenhum treino adicionado
          </p>
          <p className="mt-1.5 px-4 text-xs text-gray-500 dark:text-gray-500 sm:mt-2 sm:text-xs md:text-sm">
            Comece criando o primeiro treino para montar este plano.
          </p>
          <button
            type="button"
            onClick={onAddPrescription}
            aria-label="Criar primeiro treino"
            className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all touch-manipulation active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/50 sm:mt-5 md:hover:bg-purple-700"
          >
            <FiPlus size={16} />
            Criar primeiro treino
          </button>
        </div>
      )}
    </section>
  );
};
